// Profile photos come straight off a phone camera: a modern handset produces a 4000x3000,
// 6-10 MB JPEG, while the biggest place we ever render one is a 96px avatar. Uploading the
// original wastes the gym's bandwidth and pushes users into a size limit for no visible gain,
// so we downscale in the browser first — the same thing LinkedIn, Slack and Google Photos do.
//
// The server-side limit stays as the real enforcement; this just means an honest phone photo
// never reaches it.

const MAX_DIMENSION = 1600; // comfortably above any avatar we render, small enough to stay light
const QUALITY = 0.85;       // visually indistinguishable at avatar sizes

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

/**
 * Returns a downscaled JPEG File, or the original file unchanged if it's already small enough,
 * isn't a raster image, or anything goes wrong. Never throws: a failed downscale must not stop
 * someone uploading a photo — the original still goes up and the server still validates it.
 */
export async function downscaleImage(file) {
  if (!file?.type?.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    // Already small and already light — re-encoding would only lose quality.
    if (scale === 1 && file.size <= 1024 * 1024) return file;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", QUALITY));
    if (!blob || blob.size >= file.size) return file; // no win, keep the original
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}
